import { msg } from "@lingui/core/macro";
import { type JSX, ErrorBoundary as SolidErrorBoundary } from "solid-js";
import { AlertTriangle, RefreshCw, RotateCcw } from "@fleet/icons";
import { cn } from "@comandero/shared";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Inline theme-aware SVG illustration for error fallback. Uses
 * `currentColor` + opacities to inherit container tone (no hardcoded
 * hardcodeados). Decorativa → `aria-hidden`.
 */
function ErrorIllustration(props: { class?: string }) {
  return (
    <svg viewBox="0 0 120 120" class={props.class} aria-hidden="true" role="presentation">
      {/* plato volcado / cloche con incidencia */}
      <circle cx="60" cy="62" r="34" fill="currentColor" opacity="0.07" />
      <circle cx="60" cy="62" r="34" fill="none" stroke="currentColor" stroke-width="2.5" />
      <path
        d="M40 62a20 20 0 0 1 40 0"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
      />
      <path d="M34 62h52" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      <circle cx="60" cy="40" r="3" fill="currentColor" />
      <path
        d="M60 24v6"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        opacity="0.7"
      />
      <path
        d="M82 30l-4 4M38 30l4 4"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        opacity="0.5"
      />
    </svg>
  );
}

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  /** Optional short title (default: generic premium). */
  title?: string;
  /** If true, fills available height and centers (page regions). */
  fill?: boolean;
  class?: string;
}

/**
 * Reusable premium fallback: illustration + title + description + actions
 * "Retry" (boundary reset) and "Reload" (hard reload). Theme-safe.
 */
function ErrorFallback(props: ErrorFallbackProps) {
  const { t } = useI18n();

  const detail = (): string => {
    if (props.error instanceof Error) {
      return props.error.message;
    }
    if (typeof props.error === "string") {
      return props.error;
    }
    return t(msg`An unexpected error has occurred.`);
  };

  return (
    <div
      role="alert"
      data-testid="error-boundary-fallback"
      class={cn(
        "flex w-full items-center justify-center p-4",
        props.fill ? "min-h-[60vh] flex-1" : "py-12",
        props.class,
      )}
    >
      <GlassSurface class="flex w-full max-w-md flex-col items-center gap-6 p-8 text-center">
        <div class="text-accent/70">
          <ErrorIllustration class="h-28 w-28" />
        </div>
        <div class="space-y-2">
          <h2 class="flex items-center justify-center gap-2 text-2xl font-bold tracking-tight">
            <AlertTriangle class="h-6 w-6 text-destructive" aria-hidden="true" />
            {props.title ?? t(msg`Something went wrong`)}
          </h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`This section could not be displayed. You can try again without losing your data.`,
            )}
          </p>
          <p class="break-words text-xs text-muted-foreground/70">{detail()}</p>
        </div>
        <div class="flex w-full gap-3">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RefreshCw class="h-4 w-4" aria-hidden="true" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RotateCcw class="h-4 w-4" aria-hidden="true" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** Optional short title for fallback. */
  title?: string;
  /** Region label (for logging). */
  region?: string;
  /** Fills available height and centers (page regions). */
  fill?: boolean;
  class?: string;
}

/**
 * Solid `<ErrorBoundary>` with reusable premium fallback. Catches errors
 * render errors in subtree so isolated failure never blanks screen.
 * Logs error with region for diagnostics.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  return (
    <SolidErrorBoundary
      fallback={(error: unknown, reset: () => void) => {
        console.error(`[ErrorBoundary${props.region ? `:${props.region}` : ""}]`, error);
        return (
          <ErrorFallback
            error={error}
            reset={reset}
            title={props.title}
            fill={props.fill}
            class={props.class}
          />
        );
      }}
    >
      {props.children}
    </SolidErrorBoundary>
  );
}
